import * as React from 'react';
import { useRef, useState, useLayoutEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import BlockIcon from '@mui/icons-material/Block';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { withTranslation } from 'react-i18next';
import useCalendarState from './hooks/useCalendarState';
import { WEEK_DAY_KEYS } from './calendarConstants';

function DragContextMenu({ t }) {
  const {
    contextMenuOpen,
    contextMenuPosition,
    selectedTimeSlot,
    employees,
    serviceBays,
    currentView,
    closeContextMenu,
    clearDragSelection,
    openScheduleDialog,
    openUnavailabilityDialog,
    openNoteDialog,
    getWeekDates,
  } = useCalendarState();

  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!contextMenuOpen || !menuRef.current) return;
    const menu = menuRef.current;
    const margin = 8;
    let x = contextMenuPosition.x;
    let y = contextMenuPosition.y;

    if (x + menu.offsetWidth > window.innerWidth - margin) {
      x = window.innerWidth - menu.offsetWidth - margin;
    }
    if (y + menu.offsetHeight > window.innerHeight - margin) {
      y = window.innerHeight - menu.offsetHeight - margin;
    }
    if (x < margin) x = margin;
    if (y < margin) y = margin;

    setAdjustedPosition({ x, y });
  }, [contextMenuOpen, contextMenuPosition]);

  if (!contextMenuOpen || !selectedTimeSlot) return null;

  const weekDates = getWeekDates();
  const resources = currentView === 'employee' ? employees : serviceBays;
  const resource = resources.find((r) => r.id === selectedTimeSlot.resourceId);

  // Support multi-day selections
  const isMultiDay = selectedTimeSlot.isMultiDay;
  const startDate = selectedTimeSlot.startDate || weekDates[selectedTimeSlot.startDayIndex ?? selectedTimeSlot.dayIndex];
  const endDate = selectedTimeSlot.endDate || weekDates[selectedTimeSlot.endDayIndex ?? selectedTimeSlot.dayIndex];

  const formatTime = (hour) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
    return `${t(WEEK_DAY_KEYS[dayIndex])} ${date.getDate()}.${date.getMonth() + 1}.`;
  };

  const actualStart = Math.min(selectedTimeSlot.startHour, selectedTimeSlot.endHour);
  const actualEnd = Math.max(selectedTimeSlot.startHour, selectedTimeSlot.endHour);

  const menuOptions = [
    {
      icon: <AddIcon />,
      label: t('Create new maintenance'),
      description: t('Create new planned maintenance'),
      color: '#3b82f6',
      onClick: () => openScheduleDialog(null, 'new'),
      testId: 'context-menu-create-new',
    },
    {
      icon: <LinkIcon />,
      label: t('Link existing maintenance'),
      description: t('Select unscheduled maintenance'),
      color: '#f59e0b',
      onClick: () => openScheduleDialog(null, 'existing'),
      testId: 'context-menu-link-existing',
    },
    {
      icon: <BlockIcon />,
      label: t('Mark as unavailable'),
      description: t('Vacation sick leave other'),
      color: '#ec4899',
      onClick: () => openUnavailabilityDialog(),
      showOnlyForEmployee: true,
      testId: 'context-menu-unavailability',
    },
    {
      icon: <StickyNote2Icon />,
      label: t('Add note'),
      description: t('Add note to calendar'),
      color: '#0ea5e9',
      onClick: () => openNoteDialog(),
      // Employee view pins the note to the selected mechanic's column; the combined view
      // writes a note for the whole workshop. Service bays have no notes of their own.
      showOnlyForNotes: true,
      testId: 'context-menu-note',
    },
  ].filter((option) => {
    if (option.showOnlyForEmployee && currentView !== 'employee') return false;
    if (option.showOnlyForNotes && currentView !== 'all' && currentView !== 'employee') return false;
    return true;
  });

  // Handle backdrop click - dismiss menu and clear overlay
  const handleBackdropClick = () => {
    closeContextMenu();
    clearDragSelection();
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={handleBackdropClick}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
        }}
      />

      {/* Menu */}
      <Paper
        ref={menuRef}
        data-testid="calendar-context-menu"
        elevation={8}
        sx={{
          position: 'fixed',
          top: adjustedPosition.y,
          left: adjustedPosition.x,
          zIndex: 1000,
          minWidth: 280,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {t('Selected time slot')}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {isMultiDay ? (
              // Multi-day format: "Ma 5.1. 7:00 - Ti 6.1. 11:00"
              `${formatDateShort(startDate)} ${formatTime(actualStart)} - ${formatDateShort(endDate)} ${formatTime(actualEnd)}`
            ) : (
              // Single-day format: "7:00 - 11:00"
              `${formatTime(actualStart)} - ${formatTime(actualEnd)}`
            )}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {isMultiDay ? (
              // Multi-day: show only resource name (empty if 'all' view)
              resource?.name || ''
            ) : (
              // Single-day: show resource name and date
              resource?.name ? `${resource.name} • ${formatDateShort(startDate)}` : formatDateShort(startDate)
            )}
          </Typography>
        </Box>

        {/* Options */}
        <Box sx={{ p: 1 }}>
          {menuOptions.map((option, idx) => (
            <Box
              key={idx}
              data-testid={option.testId}
              onClick={option.onClick}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background 0.1s ease',
                '&:hover': {
                  bgcolor: 'background.default',
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: `${option.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: option.color,
                  flexShrink: 0,
                }}
              >
                {option.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {option.label}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {option.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
}

export default withTranslation()(DragContextMenu);
